/**
 * "Shared with" card: lets the owner (or an admin) add/remove collaborators from the
 * roster, giving them read access (and, while the matter is still a draft, edit access
 * too — see isMatterOwner in firestore.rules). Writes ONLY collaboratorUids (+
 * updatedAt) directly from the client, which firestore.rules allows for owner/admin
 * regardless of matter status — see isOnlySharingChange().
 */

import { useState } from 'react';
import { doc, serverTimestamp, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';

export default function SharedWithCard({ matterId, matter, roster, canEdit, defaultOpen = true }) {
  const collaboratorUids = Array.isArray(matter.collaboratorUids) ? matter.collaboratorUids : [];
  // Matter Detail keeps its established always-open presentation via defaultOpen.
  // Intake opts into a compact initial state, except when sharing is already in use.
  const [open, setOpen] = useState(() => defaultOpen || collaboratorUids.length > 0);
  const [query, setQuery] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const activeRoster = roster.filter((u) => u.isActive !== false);
  const collaborators = collaboratorUids
    .map((uid) => activeRoster.find((u) => u.id === uid) || roster.find((u) => u.id === uid))
    .filter(Boolean);

  const q = query.trim().toLowerCase();
  const matches =
    q.length === 0
      ? []
      : activeRoster
          .filter((u) => u.id !== matter.createdBy && !collaboratorUids.includes(u.id))
          .filter(
            (u) =>
              (u.displayName || '').toLowerCase().includes(q) ||
              (u.email || '').toLowerCase().includes(q)
          )
          .slice(0, 8);

  const save = async (nextUids) => {
    setBusy(true);
    setError('');
    try {
      await updateDoc(doc(db, 'matters', matterId), {
        collaboratorUids: nextUids,
        updatedAt: serverTimestamp(),
      });
    } catch (err) {
      setError(err.message || String(err));
    } finally {
      setBusy(false);
    }
  };

  const add = (uid) => {
    setQuery('');
    save([...collaboratorUids, uid]);
  };
  const remove = (uid) => save(collaboratorUids.filter((id) => id !== uid));

  return (
    <section className="rounded border border-slate-200 dark:border-slate-700 bg-slate-50/60 dark:bg-slate-800/40 px-4 py-2.5">
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-xs font-medium text-slate-500 dark:text-slate-400">Shared with</h2>
        <button
          type="button"
          onClick={() => setOpen((current) => !current)}
          className="rounded px-2 py-0.5 text-xs text-slate-500 dark:text-slate-400 hover:bg-slate-200/60 dark:hover:bg-slate-700/60 hover:text-slate-700 dark:hover:text-slate-200"
          aria-expanded={open}
        >
          {open ? 'Collapse' : canEdit ? '+ Add' : 'Show'}
        </button>
      </div>

      {open && (
        <>
          <p className="mt-1 text-xs text-slate-400 dark:text-slate-500">
            People added here can view this matter (and edit it while it is still a draft).
          </p>

          {collaborators.length > 0 ? (
            <div className="mt-2 flex flex-wrap gap-1">
              {collaborators.map((u) => (
                <span
                  key={u.id}
                  className="inline-flex items-center gap-1 rounded bg-firm-50 dark:bg-firm-900 px-2 py-0.5 text-xs text-firm-700 dark:text-firm-100"
                >
                  {u.displayName || u.email}
                  {canEdit && (
                    <button
                      type="button"
                      disabled={busy}
                      className="text-firm-500 dark:text-firm-100 hover:text-red-600 dark:hover:text-red-400"
                      onClick={() => remove(u.id)}
                    >
                      ×
                    </button>
                  )}
                </span>
              ))}
            </div>
          ) : (
            <p className="mt-2 text-xs text-slate-400 dark:text-slate-500">Not shared with anyone yet.</p>
          )}

          {canEdit && (
            <div className="relative mt-2 max-w-sm">
              <input
                className="w-full rounded border border-slate-300 dark:border-slate-600 px-3 py-1.5 text-sm"
                placeholder="Search people to add…"
                value={query}
                disabled={busy}
                onChange={(e) => setQuery(e.target.value)}
              />
              {matches.length > 0 && (
                <ul className="absolute z-10 mt-1 w-full rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm shadow-lg">
                  {matches.map((u) => (
                    <li key={u.id}>
                      <button
                        type="button"
                        className="block w-full px-3 py-1.5 text-left hover:bg-firm-50 dark:hover:bg-firm-900"
                        onClick={() => add(u.id)}
                      >
                        {u.displayName}
                        <span className="ml-1 text-xs text-slate-500 dark:text-slate-400">{u.email}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
          {error && <p className="mt-2 text-xs text-red-600 dark:text-red-400">{error}</p>}
        </>
      )}
    </section>
  );
}
